/**
 * File: use_categories_fetch.ts
 * Description: Hook that fetches FAQ categories from a given endpoint via axios-hooks' useAxios,
 * exposing the reactive fetch state shared by CategoriesProvider (public interface) and AdminPage.
 */
import useAxios from 'axios-hooks';
import type { Category, CategoriesState } from '@/interfaces/category';

/**
 * Fetches FAQ categories from the given endpoint and exposes the reactive fetch state.
 * Wraps useAxios so callers don't depend on axios-hooks directly. Must be called inside a
 * React component or another hook, following the Rules of Hooks.
 *
 * @param {string} endpoint - The categories endpoint to fetch from (public or admin).
 * @returns {CategoriesState} The current categories, loading flag, error, and refetch function.
 */
export function useCategoriesFetch(endpoint: string): CategoriesState {
  const [{ data, loading, error }, refetch] = useAxios<{ categories: Category[] }>(endpoint);

  return {
    categories: data?.categories ?? [],
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}
